/**
 * Controller para la página de planes del consumidor.
 *
 * - Muestra skeleton mientras se carga el frame plans_list
 * - Actualiza la URL con history.replaceState al cambiar duration_type o service_group_id
 * - Navega al frame plans_list al cambiar los selectores (sin recargar toda la página)
 */
import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const PLANS_FRAME_ID = "plans_list"

export default class extends Controller {
  static targets = ["skeleton", "durationSelectContainer", "categorySelectContainer"]
  static values = { basePath: String }

  connect() {
    this.boundShowSkeleton = this.showSkeleton.bind(this)
    this.boundHideSkeletonAndSync = this.hideSkeletonAndSync.bind(this)
    this.boundHideSkeleton = this.hideSkeleton.bind(this)

    document.documentElement.addEventListener("turbo:before-fetch-request", this.boundShowSkeleton)
    document.documentElement.addEventListener("turbo:frame-load", this.boundHideSkeletonAndSync)
    document.documentElement.addEventListener("turbo:fetch-request-error", this.boundHideSkeleton)
  }

  disconnect() {
    document.documentElement.removeEventListener("turbo:before-fetch-request", this.boundShowSkeleton)
    document.documentElement.removeEventListener("turbo:frame-load", this.boundHideSkeletonAndSync)
    document.documentElement.removeEventListener("turbo:fetch-request-error", this.boundHideSkeleton)
  }

  showSkeleton(event) {
    if (!this.hasSkeletonTarget) return
    const header = this._turboFrameHeaderFromEvent(event)
    const matchesHeader = header === PLANS_FRAME_ID
    const matchesTarget = !header && this._isPlansListRequest(event.target)
    if (!matchesHeader && !matchesTarget) return
    this.skeletonTarget.classList.remove("hidden")
    this._syncFromUrl(event.detail?.url)
  }

  _turboFrameHeaderFromEvent(event) {
    const headers = event.detail?.fetchOptions?.headers
    if (!headers) return null
    if (typeof headers.get === "function") return headers.get("Turbo-Frame")
    return headers["Turbo-Frame"]
  }

  hideSkeleton(event) {
    if (event.target?.id !== PLANS_FRAME_ID || !this.hasSkeletonTarget) return
    this.skeletonTarget.classList.add("hidden")
  }

  hideSkeletonAndSync(event) {
    this.hideSkeleton(event)
    this._syncFromFrame(event.target)
  }

  navigateOnFilterChange() {
    if (!this.hasBasePathValue) return
    const url = this._buildPlansUrlFromSelects()
    Turbo.visit(url, { frame: PLANS_FRAME_ID })
  }

  _syncFromUrl(url) {
    if (!url) return
    const params = new URL(url.toString()).searchParams
    const durationType = params.get("duration_type") || ""
    const serviceGroupId = params.get("service_group_id") || ""
    this._updateFilterSelects(durationType, serviceGroupId)
  }

  _syncFromFrame(frame) {
    if (frame?.id !== PLANS_FRAME_ID) return
    const meta = frame.querySelector("[data-plans-meta]")
    const durationType = meta?.dataset?.durationType ?? ""
    const serviceGroupId = meta?.dataset?.serviceGroupId ?? ""
    this._updateFilterSelects(durationType, serviceGroupId)
  }

  _updateFilterSelects(durationType, serviceGroupId) {
    if (this.hasDurationSelectContainerTarget) {
      const select = document.getElementById("plans_duration_type_filter")
      if (select && select.value !== durationType) select.value = durationType
    }
    if (this.hasCategorySelectContainerTarget) {
      const select = document.getElementById("plans_service_group_filter")
      if (select && select.value !== serviceGroupId) select.value = serviceGroupId
    }
    this._updateHistoryState(durationType, serviceGroupId)
  }

  _updateHistoryState(durationType, serviceGroupId) {
    if (!this.hasBasePathValue) return
    const url = this._buildPlansUrl({ durationType, serviceGroupId })
    window.history.replaceState({ turboFrame: PLANS_FRAME_ID }, "", url)
  }

  _isPlansListRequest(target) {
    return target?.id === PLANS_FRAME_ID || target?.dataset?.turboFrame === PLANS_FRAME_ID
  }

  _buildPlansUrlFromSelects() {
    const durationSelect = document.getElementById("plans_duration_type_filter")
    const categorySelect = document.getElementById("plans_service_group_filter")
    const durationType = durationSelect?.value || ""
    const serviceGroupId = categorySelect?.value || ""
    return this._buildPlansUrl({ durationType, serviceGroupId })
  }

  _buildPlansUrl({ durationType, serviceGroupId }) {
    const url = new URL(this.basePathValue, window.location.origin)
    if (durationType) {
      url.searchParams.set("duration_type", durationType)
    } else {
      url.searchParams.delete("duration_type")
    }
    if (serviceGroupId) {
      url.searchParams.set("service_group_id", serviceGroupId)
    } else {
      url.searchParams.delete("service_group_id")
    }
    return url.toString()
  }
}
